import React from "react";
import { RouteObject, redirect } from "react-router";
import { generateReportRoutes } from "@sscs/report/report";
import { LoadingDots, PageNotFound } from "@sscs/ui";
import { HelpPage, PublicLayout } from "Components";
import {
  ACCEPT_INVITE_ROUTE,
  AI_CONFIGURATION_ROUTE,
  APPEARANCE_ROUTE,
  AUDIT_ROUTE,
  CATALOG_ROUTE,
  CHANGE_PASSWORD_ROUTE,
  CLI_CAPACITY_ROUTE,
  COOKIE_POLICY_ROUTE,
  EMAIL_CONFIRMATION_ROUTE,
  FILESTREAM_ROUTE,
  FORBIDDEN_ROUTE,
  FULL_REPORT_PATH,
  GROUPS_ROUTE,
  GROUP_MAPPING_ROUTE,
  HELP_ROUTE,
  INSIGHTS_ROUTE,
  INSIGHTS_SEARCH_ROUTE,
  LICENSING_ROUTE,
  LOGIN_ROUTE,
  MEMBERS_ROUTE,
  OLD_REPORT_PATH,
  PACKAGE_ABOUT_ROUTE,
  PACKAGE_ROUTE,
  PACKAGE_VERSIONS_ROUTE,
  PAGE_NOT_FOUND_ROUTE,
  PASSWORD_RESET_ROUTE,
  PERSONAL_ACCESS_TOKEN_ROUTE,
  POLICY_CONFIG_ROUTE,
  PROFILE_ROUTE,
  PROJECTS_ROUTE,
  PROJECT_ROUTE,
  REGISTER_USER_ROUTE,
  REQUEST_PASSWORD_RESET_ROUTE,
  SESSION_CONFIGURATION_ROUTE,
  SETTINGS_ROUTE,
  SHAREABLE_REPORT_PATH,
  SHARED_REPORT_NOT_FOUND_ROUTE,
  SOFTWARE_CATALOG_PLATFORM_ROUTE,
  SOFTWARE_CATALOG_ROUTE,
  USER_DIRECTORY_ROUTE,
  USER_SETTINGS_ROUTE,
  VERIFY_SSO_CHANGES_ROUTE,
  VERIFY_SSO_LOGIN_ROUTE,
} from "Constants";
import { Administration } from "Pages/administration";
import { AiSettings } from "Pages/aiSettings";
import { AuditLogs } from "Pages/audit";
import { CookiePolicy } from "Pages/cookiePolicy";
import { FileStream } from "Pages/filestream";
import { Insights, InsightsIndexRedirect, Search } from "Pages/insights";
import { Layout } from "Pages/layout";
import { CliCapacity, Licensing } from "Pages/licensing";
import { Login, SupportLogin, VerifyOIDCLogin } from "Pages/login";
import {
  AcceptInvite,
  GroupsTable,
  MembersPage,
  MembersTable,
  RegisterMember,
} from "Pages/members";
import {
  ChangePassword,
  EmailConfirmation,
  RequestReset,
} from "Pages/passwordReset";
import { PolicyConfiguration } from "Pages/policyConfig";
import { Report } from "Pages/portalReport";
import {
  About,
  PackageLayout,
  ProjectsContent,
  ProjectsLayout,
  ProjectsTab,
  Versions,
} from "Pages/projects";
import { PublicReport, SharedReportNotFound } from "Pages/publicReport";
import { SessionConfig } from "Pages/sessionConfig";
import {
  CatalogLayout,
  Package,
  Platform,
  SoftwareCatalog,
} from "Pages/softwareCatalog";
import {
  GroupMapping,
  UserDirectory,
  UserDirectoryPage,
  VerifySSOChanges,
} from "Pages/userDirectory";
import {
  Appearance,
  ChangeMyPassword,
  PersonalAccessTokens,
  Profile,
  UserSettings,
} from "Pages/userSettings";
import {
  areAnalyticsUsed,
  areInsightsEnabled,
  checkIsDemoPortal,
  isGtmUsed,
} from "Utils";
import { generateBackwardsCompatibleRoutes } from "./BackwardsCompatible.routes";
import RedirectToNewReportURL from "./RedirectToNewReportURL";

const insightsLoader = () =>
  areInsightsEnabled() ? null : redirect(`/${PAGE_NOT_FOUND_ROUTE}`);

export const generateAppRoutes = (): RouteObject[] => [
  {
    path: `/${REQUEST_PASSWORD_RESET_ROUTE}`,
    element: <RequestReset />,
  },
  {
    path: `/${EMAIL_CONFIRMATION_ROUTE}/:verificationToken`,
    element: <EmailConfirmation />,
  },
  {
    path: `/${ACCEPT_INVITE_ROUTE}/:inviteToken`,
    element: <AcceptInvite />,
  },
  {
    path: `/${PASSWORD_RESET_ROUTE}/:verificationToken`,
    element: <ChangePassword />,
  },
  {
    path: `/${VERIFY_SSO_CHANGES_ROUTE}`,
    element: <VerifySSOChanges />,
  },
  {
    path: `/${REGISTER_USER_ROUTE}`,
    element: <RegisterMember />,
    hydrateFallbackElement: <LoadingDots />,
    loader: () => {
      const isDemoPortal = checkIsDemoPortal();
      if (!isDemoPortal) {
        return redirect(PAGE_NOT_FOUND_ROUTE);
      }
      return null;
    },
  },
  { path: `/${LOGIN_ROUTE}`, element: <Login /> },
  { path: `/${LOGIN_ROUTE}/support`, element: <SupportLogin /> },
  {
    path: `/${LOGIN_ROUTE}/${VERIFY_SSO_LOGIN_ROUTE}`,
    element: <VerifyOIDCLogin />,
  },
  {
    path: "/",
    element: <PublicLayout />,
    children: [
      ...generateReportRoutes(SHAREABLE_REPORT_PATH, <PublicReport />),
      {
        path: `/${SHARED_REPORT_NOT_FOUND_ROUTE}`,
        element: <SharedReportNotFound />,
      },
    ],
  },
  {
    path: "/",
    element: <Layout />,
    children: [
      ...generateBackwardsCompatibleRoutes(),
      {
        index: true,
        element: <FileStream />,
      },
      {
        path: FILESTREAM_ROUTE,
        element: <FileStream />,
      },
      {
        path: `${FILESTREAM_ROUTE}/group/:groupId`,
        element: <FileStream />,
      },
      {
        path: HELP_ROUTE,
        element: <HelpPage />,
      },
      ...generateReportRoutes(`${FULL_REPORT_PATH}/*`, <Report />),
      {
        path: OLD_REPORT_PATH,
        element: <RedirectToNewReportURL />,
      },
      {
        path: PROJECTS_ROUTE,
        element: <ProjectsTab />,
      },
      {
        path: `${PROJECTS_ROUTE}/group/:groupId`,
        element: <ProjectsTab />,
        children: [
          {
            path: `${PROJECT_ROUTE}/:projectId`,
            element: <ProjectsLayout />,
            children: [
              {
                index: true,
                element: <ProjectsContent />,
              },
              {
                path: `${PACKAGE_ROUTE}/:packageId`,
                element: <PackageLayout />,
                children: [
                  { index: true, element: <Versions /> },
                  {
                    path: PACKAGE_ABOUT_ROUTE,
                    element: <About />,
                  },
                  {
                    path: PACKAGE_VERSIONS_ROUTE,
                    element: <Versions />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: INSIGHTS_ROUTE,
        element: <Insights />,
        hydrateFallbackElement: <LoadingDots />,
        loader: insightsLoader,
      },
      {
        path: `${INSIGHTS_ROUTE}/group/:groupId`,
        element: <Insights />,
        hydrateFallbackElement: <LoadingDots />,
        loader: insightsLoader,
        children: [
          {
            index: true,
            element: <InsightsIndexRedirect />,
          },
          {
            path: `${INSIGHTS_SEARCH_ROUTE}/:searchId?`,
            element: <Search />,
            hydrateFallbackElement: <LoadingDots />,
            loader: insightsLoader,
          },
        ],
      },
      {
        path: POLICY_CONFIG_ROUTE,
        element: <PolicyConfiguration configScope="group" />,
      },
      {
        path: `${POLICY_CONFIG_ROUTE}/group/:groupId`,
        element: <PolicyConfiguration configScope="group" />,
      },
      {
        path: SOFTWARE_CATALOG_ROUTE,
        element: <SoftwareCatalog />,
      },
      {
        path: `${SOFTWARE_CATALOG_ROUTE}/group/:groupId`,
        element: <SoftwareCatalog />,
        children: [
          {
            path: `${CATALOG_ROUTE}/:catalogId`,
            children: [
              { index: true, element: <CatalogLayout /> },
              {
                path: `${SOFTWARE_CATALOG_PLATFORM_ROUTE}/:platform`,
                element: <CatalogLayout />,
                children: [
                  {
                    index: true,
                    element: <Platform />,
                  },
                  {
                    path: `${PACKAGE_ROUTE}/:packageId`,
                    element: <Package />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <Administration />,
        children: [
          {
            path: "organization/:organizationId",
            children: [
              {
                path: POLICY_CONFIG_ROUTE,
                element: <PolicyConfiguration configScope="organization" />,
              },
              { path: LICENSING_ROUTE, element: <Licensing /> },
              { path: CLI_CAPACITY_ROUTE, element: <CliCapacity /> },
              {
                path: MEMBERS_ROUTE,
                element: <MembersPage />,
                children: [
                  {
                    path: `${GROUPS_ROUTE}/:membersGroupId?`,
                    element: <GroupsTable />,
                  },
                  { path: "", element: <MembersTable /> },
                ],
              },
              { path: SESSION_CONFIGURATION_ROUTE, element: <SessionConfig /> },
              { path: AI_CONFIGURATION_ROUTE, element: <AiSettings /> },
              { path: AUDIT_ROUTE, element: <AuditLogs /> },
              {
                path: "*",
                element: (
                  <PageNotFound
                    title="We can't find that page"
                    byline="Try selecting a page from the sidebar"
                  />
                ),
              },
            ],
          },
          {
            // SSO route should not be changed because URL is used to configure identity provider
            // https://docs.secure.software/portal/sso-configuration#1-configure-sso-on-the-identity-provider-side
            path: SETTINGS_ROUTE,
            children: [
              {
                path: USER_DIRECTORY_ROUTE,
                element: <UserDirectoryPage />,
                children: [
                  { index: true, element: <UserDirectory /> },
                  { path: GROUP_MAPPING_ROUTE, element: <GroupMapping /> },
                ],
              },
              {
                path: "*",
                element: (
                  <PageNotFound
                    title="We can't find that page"
                    byline="Try selecting a page from the sidebar"
                  />
                ),
              },
            ],
          },
        ],
      },
      {
        path: USER_SETTINGS_ROUTE,
        element: <UserSettings />,
        children: [
          { path: PROFILE_ROUTE, element: <Profile /> },
          { path: CHANGE_PASSWORD_ROUTE, element: <ChangeMyPassword /> },
          {
            path: PERSONAL_ACCESS_TOKEN_ROUTE,
            element: <PersonalAccessTokens />,
          },
          { path: APPEARANCE_ROUTE, element: <Appearance /> },
          {
            path: "*",
            element: (
              <PageNotFound
                title="We can't find that page"
                byline="Try selecting a page from the sidebar"
              />
            ),
          },
        ],
      },
      {
        path: `/${COOKIE_POLICY_ROUTE}`,
        element: (
          <CookiePolicy
            analyticsUsed={areAnalyticsUsed()}
            gtmUsed={isGtmUsed()}
          />
        ),
      },
      {
        path: PAGE_NOT_FOUND_ROUTE,
        element: (
          <PageNotFound
            title="Page not found"
            byline="The address may be mistyped or the page may have moved. Use the group menu in the top bar to find your way back."
          />
        ),
      },
      {
        path: FORBIDDEN_ROUTE,
        element: (
          <PageNotFound
            title="You do not have rights to view this page"
            byline="Contact organization administrator for more information"
          />
        ),
      },
      {
        path: "*",
        element: (
          <PageNotFound
            title="Page not found"
            byline="The address may be mistyped or the page may have moved. Use the group menu in the top bar to find your way back."
          />
        ),
      },
    ],
  },
];
